// One wash, in a dialog: the Washing Plan opens it from a wash on the calendar or in "To plan", and the Service
// page from a vessel's Washing button. It shows the boat and why the wash is needed, and lets someone with
// edit access plan it on a day (or take it off the plan), tick it washed and say on which day (Washed on: the
// day it was really washed, not the day it was ticked), mark it as needing no wash at all
// (a service boat that came in clean, or one the yard has no time for while new boats come first), and note
// it for the floor. "No washing required" takes the wash off the plan and out of "To plan", with who said so
// and when, until "Needs washing after all" puts it back. It also says how long the wash takes, by the boat's
// model: the outside and the interior (WASH_TIMES in packages/shared domain/wash.ts).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { WASH_KIND_INFO, washTime, washTotal, type WashInput, type WashKind, type WashRecord, type WashSource } from '@technohull/shared';
import { daysBetween, washRangeText, type WashBoat, type WashItem, type WashWhy } from '../features/washPlan.js';
import { useNameResolver } from '../features/directory.js';
import { useDateFormatter } from '../lib/format.js';
import { DateField } from './DateField.js';
import { Modal } from './ui.js';
import { IconTrash } from './icons.js';

const LineIcon = ({ size = 14, children }: { size?: number; children: ReactNode }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
    {children}
  </svg>
);
const DROP = 'M12 3.5c3.3 4.2 6 7.6 6 10.8a6 6 0 0 1-12 0c0-3.2 2.7-6.6 6-10.8z';
/** a drop of water: a wash */
export const IconDrop = ({ size }: { size?: number }) => <LineIcon size={size}><path d={DROP} /><path d="M9.2 14.6a2.9 2.9 0 0 0 2.6 2.6" /></LineIcon>;
/** a drop with a tick: washed */
export const IconDropDone = ({ size }: { size?: number }) => <LineIcon size={size}><path d={DROP} /><path d="M9.3 14.4l2 2 3.6-3.9" /></LineIcon>;
/** a drop struck through: no washing required */
export const IconDropOff = ({ size }: { size?: number }) => <LineIcon size={size}><path d={DROP} /><path d="M4 4l16 16" /></LineIcon>;
/** a clock: how long washing takes */
export const IconClock = ({ size }: { size?: number }) => <LineIcon size={size}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></LineIcon>;

export const plusDays = (day: string, n: number) => new Date(Date.parse(`${day}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** what the dialog (and the Washing Plan's calendar) knows about one wash: its record (once there is one),
 *  why it's needed (while the sheets call for it) and its boat (while it's on the Overview / Service sheet) */
export interface WashEntry { id: string; source: WashSource; boatKey: string; kind: WashKind; name: string; model: string; sap: string; record?: WashRecord; item?: WashItem; boat?: WashBoat }
export const washEntryOf = (item?: WashItem, record?: WashRecord, boat?: WashBoat): WashEntry => ({
  id: (item?.id ?? record?.id)!,
  source: (item?.source ?? record?.source)!,
  boatKey: (item?.boatKey ?? record?.boatKey)!,
  kind: (item?.kind ?? record?.kind)!,
  name: item?.name ?? record?.boatName ?? '',
  model: item?.model ?? record?.boatModel ?? '',
  sap: item?.sap ?? boat?.sap ?? (record?.source === 'prod' ? record.boatKey : ''),
  record: record ?? item?.record,
  item,
  boat,
});

/** a change to the wash the dialog is open on (the boat and the wash are filled in by the caller) */
export type WashChange = Pick<WashInput, 'plannedOn' | 'done' | 'doneOn' | 'notNeeded' | 'note'>;

export type WashDueTone = 'late' | 'soon' | 'wait' | '';
/** the words for a wash: a day, why it's needed, and when it's due */
export function useWashWords(today: string) {
  const df = useDateFormatter();
  const fmt = (d: string) => (d ? df.date(d, { year: 'auto' }) : '');
  const whyText = (w: WashWhy): string => {
    switch (w.k) {
      case 'seaDone': return w.date ? `Sea trial done ${fmt(w.date)}` : 'Sea trial done';
      case 'seaOn': return `Sea trial on ${fmt(w.date)}`;
      case 'seaPending': return 'Waiting for the sea trial';
      case 'beforePolish': return w.delivery ? `Before polishing · delivery ${fmt(w.delivery)}` : 'Before polishing';
      case 'rewash': return `Washed ${fmt(w.washed)}, ${w.days} days before delivery ${fmt(w.delivery)}`;
      case 'lastSeason': return w.delivery ? `Last season's boat, washed then · delivery ${fmt(w.delivery)}` : 'Last season\'s boat, washed then';
      case 'serviceDone': return w.delivery ? `Service finished · back to the client ${fmt(w.delivery)}` : 'Service finished';
    }
  };
  const due = (i: WashItem): { text: string; tone: WashDueTone } => {
    if (!i.ready) return { text: i.why.k === 'seaOn' ? `After the sea trial, ${fmt(i.why.date)}` : 'Waits on the sea trial', tone: 'wait' };
    if (!i.dueBy) return { text: 'Ready now', tone: '' };
    const n = daysBetween(today, i.dueBy);
    if (n < 0) return { text: `Overdue · was due ${fmt(i.dueBy)}`, tone: 'late' };
    if (n === 0) return { text: 'Due today', tone: 'late' };
    if (n <= 7) return { text: `Due in ${plural(n, 'day')} · ${fmt(i.dueBy)}`, tone: 'soon' };
    return { text: `Due by ${fmt(i.dueBy)}`, tone: '' };
  };
  return { fmt, whyText, due };
}

/** one wash: when it's planned for, washed or not needed, its note; and the boat it's for. `context` says
 *  where the boat stands when the sheets don't call for the wash (yet), e.g. a service still in progress. */
export function WashDialog({ entry, editable, today, context, onChange, onClose }: {
  entry: WashEntry;
  editable: boolean;
  today: string;
  context?: string;
  onChange: (v: WashChange) => void;
  onClose: () => void;
}) {
  const nameOf = useNameResolver();
  const { fmt, whyText, due } = useWashWords(today);
  const info = WASH_KIND_INFO[entry.kind];
  const r = entry.record;
  const [note, setNote] = useState(r?.note ?? '');
  const planned = r?.plannedOn ?? '';
  const done = !!r?.doneOn;
  const skipped = !done && !!r?.notNeededOn;
  const nextMonday = (() => { const w = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7; return plusDays(today, 7 - w); })();
  const quick: Array<[string, string]> = [['Today', today], ['Tomorrow', plusDays(today, 1)], ['Next Monday', nextMonday]];
  if (entry.item?.why.k === 'seaOn' && entry.item.why.date >= today) quick.push(['Day after the sea trial', plusDays(entry.item.why.date, 1)]);
  const it = entry.item;
  const boat = entry.boat;
  const pct = it?.pct ?? boat?.pct ?? null;
  const delivery = it?.delivery || boat?.delivery || '';
  const dueText = it && !done && !skipped ? due(it).text : '';
  const time = washTime(entry.model);
  // marking it not needed (or needed after all) swaps the button pressed for another: keep the focus in the
  // dialog, on what's there now, so the keyboard (and Escape) still work
  const outcome = useRef<HTMLDivElement>(null);
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    outcome.current?.querySelector<HTMLElement>('button, input')?.focus();
  }, [skipped]);
  return (
    <Modal
      title={entry.name || 'Wash'}
      dataTitle
      onClose={onClose}
      width={560}
      className="wp-dlg"
      footer={
        <>
          {editable && planned && !done && !skipped && (
            <button type="button" className="btn btn-ghost btn-del" onClick={() => { onChange({ plannedOn: null }); onClose(); }}><IconTrash size={14} /> Take off the plan</button>
          )}
          <button type="button" className="btn" onClick={onClose} style={{ marginLeft: 'auto' }}>Close</button>
        </>
      }
    >
      <div className={`wp-dlg-kind wp-k-${entry.kind}`}>
        <span className={`wp-badge wp-k-${entry.kind} wp-badge-lg`} aria-hidden="true">{info.n}</span>
        <span><b>{info.name}</b><span className="wp-el" lang="el">{info.nameEl}</span></span>
      </div>
      <dl className="wp-facts">
        {entry.model && <><dt>Model</dt><dd>{entry.model}</dd></>}
        {time && (
          <><dt>Washing time</dt><dd><b>{washRangeText(washTotal(time))}</b><span className="wp-time-split">Outside {washRangeText(time.outside)} · Interior {washRangeText(time.interior)}</span></dd></>
        )}
        {!time && entry.model && <><dt>Washing time</dt><dd className="muted">Not set for the {entry.model}</dd></>}
        {entry.sap && <><dt>SAP</dt><dd>{entry.sap}</dd></>}
        {pct != null && <><dt>Assembly</dt><dd>{Math.round(pct)}%</dd></>}
        {delivery && <><dt>Delivery</dt><dd>{fmt(delivery)}</dd></>}
        {it && <><dt>Why</dt><dd>{whyText(it.why)}</dd></>}
        {dueText && <><dt>When</dt><dd>{dueText}</dd></>}
        {!it && !done && !skipped && context && <><dt>Status</dt><dd>{context}</dd></>}
        {!it && !done && !skipped && !context && r && <><dt>Note</dt><dd>The {entry.source === 'prod' ? 'Overview' : 'Service sheet'} no longer calls for this wash (done another way, or the boat has moved on); it stays on the plan until it's taken off.</dd></>}
      </dl>

      <div ref={outcome}>
      {skipped ? (
        <div className="wp-dlg-sec">
          <div className="wp-skipbox on" role="status">
            <IconDropOff size={20} />
            <span>
              <b>No washing required <span className="wp-el" lang="el">Δεν χρειάζεται πλύσιμο</span></b>
              <small>{fmt(r!.notNeededOn)}{r?.notNeededBy ? ` · ${nameOf(r.notNeededBy)}` : ''}</small>
            </span>
            {editable && <button type="button" className="btn btn-sm" onClick={() => onChange({ notNeeded: false })}>Needs washing after all</button>}
          </div>
        </div>
      ) : (
        <>
          <div className="wp-dlg-sec">
            <span className="field-label">Planned for <span className="wp-el" lang="el">Ημερομηνία</span></span>
            {editable && !done ? (
              <>
                <DateField value={planned} onCommit={(v) => onChange({ plannedOn: v })} title="Planned for" />
                <div className="wp-quick">
                  {quick.map(([label, day]) => (
                    <button key={label} type="button" className={`btn btn-sm${planned === day ? ' is-on' : ''}`} onClick={() => onChange({ plannedOn: day })}>{label}<small>{fmt(day)}</small></button>
                  ))}
                </div>
              </>
            ) : <span>{planned ? fmt(planned) : 'Not planned'}</span>}
          </div>

          <div className="wp-dlg-sec wp-outcome">
            <label className={`wp-donebox${done ? ' on' : ''}`}>
              <input
                type="checkbox"
                checked={done}
                disabled={!editable}
                onChange={(e) => onChange(e.currentTarget.checked ? { done: true, doneOn: planned && planned <= today ? planned : today } : { done: false })}
              />
              <span>
                <b>{done ? 'Washed' : 'Mark as washed'} <span className="wp-el" lang="el">{done ? 'Πλύθηκε' : 'Έγινε'}</span></b>
                {done && r?.doneBy && <small>Recorded by {nameOf(r.doneBy)}</small>}
              </span>
            </label>
            {editable && !done && (
              <button type="button" className="wp-skipbox" onClick={() => onChange({ notNeeded: true })}>
                <IconDropOff size={18} />
                <span>
                  <b>No washing required</b>
                  <span className="wp-el" lang="el">Δεν χρειάζεται πλύσιμο</span>
                  <small>Takes it off the plan and the list to plan</small>
                </span>
              </button>
            )}
          </div>
          {done && (
            <div className="wp-dlg-sec">
              <label className="field-label" htmlFor="wp-washed-on">Washed on <span className="wp-el" lang="el">Ημερομηνία πλυσίματος</span></label>
              {editable ? (
                <>
                  <DateField
                    key={r!.doneOn}
                    id="wp-washed-on"
                    value={r!.doneOn}
                    max={today}
                    onCommit={(v) => { if (v && v !== r!.doneOn) onChange({ done: true, doneOn: v }); }}
                    title="The day it was washed"
                  />
                  <small className="muted wp-hint-sm">The day the boat was actually washed, not the day it was ticked: change it if it was another day.</small>
                </>
              ) : <span>{fmt(r!.doneOn)}</span>}
            </div>
          )}
        </>
      )}
      </div>

      <div className="wp-dlg-sec">
        <label className="field-label" htmlFor="wp-note">Note for the floor <span className="wp-el" lang="el">Σημείωση</span></label>
        <textarea
          id="wp-note"
          className="field"
          rows={2}
          maxLength={300}
          value={note}
          disabled={!editable}
          placeholder={editable ? (skipped ? 'e.g. came in clean, washed by the client' : 'e.g. hull only, the deck is covered') : ''}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => { if (note.trim() !== (r?.note ?? '')) onChange({ note: note.trim() }); }}
        />
      </div>
      {!editable && <p className="muted wp-ro">You can see the plan; changing it needs {entry.source === 'prod' ? 'Overview' : 'Service'} edit access.</p>}
    </Modal>
  );
}
